import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CredentialDialog } from '../../components/CredentialDialog';
import { useNavigate } from 'react-router-dom';

interface SubSellerItem {
  id: string;
  parentId: string;
  name: string;
  payinSharePercent: number;
  payoutSharePercent: number;
  level: number;
  status: string;
}

export function SubSellerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [credential, setCredential] = useState<{
    username: string;
    tempPassword: string;
    subSellerName: string;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () => apiGet<{ items: SubSellerItem[]; total: number }>('/seller/sub-sellers'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiPatch(`/seller/sub-sellers/${id}`, { status: 'inactive' }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => apiPatch(`/seller/sub-sellers/${id}`, { status: 'active' }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ username: string; tempPassword: string; subSellerName: string }>(
        `/seller/sub-sellers/${id}/reset-password`,
      ),
    onSuccess: (res) => {
      if (res.ok) {
        setCredential(res.data);
      } else {
        setError(res.error.message);
      }
    },
  });

  const subSellers = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => navigate('/seller/sub-sellers/create')}>
          {t('sellers.addSubSeller')}
        </Button>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      <Card>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && subSellers.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {subSellers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sellers.name')}</TableHead>
                  <TableHead>{t('seller.level')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payinFee')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payoutFee')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subSellers.map((ss) => (
                  <TableRow key={ss.id}>
                    <TableCell>
                      <span style={{ paddingLeft: `${(ss.level - 1) * 16}px` }}>{ss.name}</span>
                    </TableCell>
                    <TableCell>{ss.level}</TableCell>
                    <TableCell className="text-right">{ss.payinSharePercent}%</TableCell>
                    <TableCell className="text-right">{ss.payoutSharePercent}%</TableCell>
                    <TableCell>
                      <Badge variant={ss.status === 'active' ? 'success' : 'danger'}>
                        {t(`common.${ss.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/seller/sub-sellers/${ss.id}/edit`)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(t('sellers.confirmReset', { name: ss.name }))) {
                              resetPasswordMutation.mutate(ss.id);
                            }
                          }}
                          disabled={resetPasswordMutation.isPending}
                        >
                          {t('sellers.resetPassword')}
                        </Button>
                        {ss.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400"
                            onClick={() => {
                              if (window.confirm(t('common.confirm'))) {
                                deactivateMutation.mutate(ss.id);
                              }
                            }}
                            disabled={deactivateMutation.isPending}
                          >
                            {t('common.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 dark:text-green-400"
                            onClick={() => activateMutation.mutate(ss.id)}
                            disabled={activateMutation.isPending}
                          >
                            {t('common.activate')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {credential && (
        <CredentialDialog
          credential={{
            name: credential.subSellerName,
            username: credential.username,
            tempPassword: credential.tempPassword,
          }}
          onClose={() => setCredential(null)}
        />
      )}
    </div>
  );
}
